import React from 'react';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';
import debounce from 'lodash.debounce';
import { Icon, Menu } from '@economic/gaudi';
import { ApplicationConfiguration, AuthorisedLink, useApplicationContext } from '@economic/react-app';
import { t, tByRoute } from '../../shared/components/l10n/l10n';

const LINK_WIDTH = 82;

const isVisible = (link, routes: ApplicationConfiguration['routes']) => {
    let to;

    if (link.props?.disclosure) {
        to = link.props?.disclosure?.props?.to;
    } else if (link.props?.to) {
        to = link.props?.to;
    }

    if (to) {
        const sanitizedRoute = to.split('?')[0];
        return routes.hasOwnProperty(`/${sanitizedRoute}`);
    }

    return false;
};

const getAuthorisedRoutes = (children: React.ReactNode, routes: ApplicationConfiguration['routes']) => {
    const visibleLinkRoutes = React.Children.toArray(children).reduce((visibleRoutes: any[], link: any) => {
        if (isVisible(link, routes)) {
            visibleRoutes.push(link.props.to);
        }

        if (Array.isArray(link)) {
            visibleRoutes.push(...link.filter(l => isVisible(link, routes)).map(l => l.props.to));
        }

        return visibleRoutes;
    }, []);

    return visibleLinkRoutes;
};

const useVisibleLinkListener = (ref: React.RefObject<HTMLElement>, children: React.ReactNode) => {
    const [boundaryRoute, setBoundary] = React.useState(null);
    const { configuration } = useApplicationContext();

    const setBoundaryRoute = () => {
        if (ref.current) {
            const boundaryRouteIndex = Math.floor(ref.current.offsetWidth / LINK_WIDTH);
            const visibleRoutes = getAuthorisedRoutes(children, configuration.routes);
            setBoundary(boundaryRouteIndex >= visibleRoutes.length ? null : visibleRoutes[boundaryRouteIndex - 1]);
        }
    };

    React.useEffect(() => {
        const setAvailableWidth = debounce(() => setBoundaryRoute(), 100);
        setBoundaryRoute();
        window.addEventListener('resize', setAvailableWidth);

        return () => {
            window.removeEventListener('resize', setAvailableWidth);
        };
    }, []);

    return boundaryRoute;
};

type PrimaryNavigationProps = React.HTMLAttributes<HTMLElement> & {
    children: React.ReactNode;
};

export const PrimaryNavigation = ({ children, ...props }: PrimaryNavigationProps) => {
    const navigation = React.useRef<HTMLElement>(null);
    const { configuration } = useApplicationContext();
    const boundaryRoute = useVisibleLinkListener(navigation, children);

    const className = cn('flex h-full overflow-hidden', props.className);

    let output = children;

    if (boundaryRoute) {
        const visibleLinks = React.Children.toArray(children);
        const hiddenLinks = visibleLinks.splice(visibleLinks.findIndex((link: any) => link.props.to === boundaryRoute));

        output = (
            <>
                {visibleLinks}
                <li>
                    <Menu
                        disclosure={
                            <button className="flex flex-col items-center justify-center h-full w-20  text-white focus:outline-none hover:outline-none">
                                <Icon name="more-solid" />
                                <span className="mt-px text-xs">{t`TOP_NAVIGATION_MORE_TEXT`}</span>
                            </button>
                        }>
                        {hiddenLinks
                            .filter((link: any) => isVisible(link, configuration.routes))
                            .map((link: any) => (
                                <AuthorisedLink
                                    key={link.props.to}
                                    to={link.props.to}
                                    as={Menu.Link}
                                    target={link.props.to.startsWith('http') ? '_blank' : '_self'}>
                                    {tByRoute(link.props.to) ?? link.props.children}
                                </AuthorisedLink>
                            ))}
                    </Menu>
                </li>
            </>
        );
    }

    return (
        <nav {...props} className={className} ref={navigation}>
            <ul className="flex mb-0 w-full space-x-px">{output}</ul>
        </nav>
    );
};

const Link = React.forwardRef(function LinkWithRef({ active: _, ...props }: any, ref: React.Ref<HTMLAnchorElement>) {
    const location = useLocation();
    const active = location.pathname.startsWith(props.href);
    const className = cn('flex flex-col items-center justify-center h-full w-20 text-xs text-white', props.className, { active });

    return (
        <li>
            <a {...props} className={className} ref={ref} />
        </li>
    );
});

PrimaryNavigation.Link = Link;
